// Demo step 1 (US-01, US-03): data entry. Each organisation downloads the template of the fields it owns for one
// product batch, fills it in, uploads it, sees every row checked, then sends it. One value can also be entered
// directly. The database stamps who and when, refuses a field it does not know or that has no acquisition method,
// and refuses a field the organisation does not own; the browser sends values only.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { readSheet } from 'read-excel-file/browser';
import writeExcelFile from 'write-excel-file/browser';
import { PageHeader } from '../components/Layout.tsx';
import { Enum, ErrorBox, fmtValue, Loading } from '../components/ui.tsx';
import { batchChain } from '../lib/chain.ts';
import { activePack, productBatches, valuesFor, whoAmI } from '../lib/data.ts';
import { operatorClient } from '../lib/supabase.ts';
import {
  COLUMNS,
  explainRefusal,
  fieldDef,
  parseSheet,
  templateRows,
  type ChainNode,
  type FieldDef,
  type Org,
  type ParsedRow,
  type TemplateRow,
} from '../lib/template.ts';
import type { NodeRow, ValueRow } from '../lib/types.ts';

const WRITER_ROLES = ['data_provider', 'quality_manager', 'regulatory_owner', 'platform_admin'];

// Demonstration examples for step 1 (demo dataset identifiers): a field the data provider's organisation owns, and a
// field whose data-dictionary entry has no acquisition method (refused by the database, DECISIONS E-48).
const EXAMPLE_ACCEPTED = {
  node_kind: 'product_batch',
  node_id: 'PB-2026-0002',
  field_id: 'STORAGE_TEMP',
  value: '5 to 25 C',
  unit: '',
  source_document: 'DEMO-SOP-STORAGE-01',
  value_status: 'final',
};
const EXAMPLE_REFUSED = {
  node_kind: 'product_batch',
  node_id: 'PB-2026-0007',
  field_id: 'EXTRACT_METHOD',
  value: 'aqueous extraction',
  unit: '',
  source_document: 'DEMO-BATCH-RECORD-07',
  value_status: 'final',
};

interface Result {
  ok: boolean;
  text: string;
}

export function Ingest() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [fields, setFields] = useState<FieldDef[] | null>(null);
  const [rules, setRules] = useState<{ rule_id: string; overlay?: string; data_points?: unknown }[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [me, setMe] = useState<{ org: string | null; roles: string[] } | null>(null);
  const [batches, setBatches] = useState<NodeRow[]>([]);
  const [chain, setChain] = useState<ChainNode[]>([]);
  const [prodCode, setProdCode] = useState<string | null>(null);
  const [values, setValues] = useState<ValueRow[]>([]);
  // the batch whose chain and values are loaded; the template is offered only for that batch
  const [loaded, setLoaded] = useState('');
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const batchId = params.get('batch') ?? batches[0]?.node_id ?? '';
  // a data provider works for its own organisation only, whatever a link asks for
  const ownOnly = !!me && me.roles.length > 0 && me.roles.every((r) => r === 'data_provider');
  const orgCode = (ownOnly ? me.org : params.get('org')) ?? me?.org ?? '';
  const org = orgs.find((o) => o.org_code === orgCode) ?? null;
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    next.set(k, v);
    setParams(next, { replace: true });
    setParsed(null);
  };

  useEffect(() => {
    void (async () => {
      try {
        const [pack, who, list, o] = await Promise.all([
          activePack(),
          whoAmI(),
          productBatches(),
          operatorClient().from('organisations').select('org_code, name, aliases').order('name'),
        ]);
        const p = pack?.pack as unknown as {
          fields: { items: FieldDef[] };
          rules: { items: { rule_id: string; overlay?: string; data_points?: unknown }[] };
        };
        setFields(p?.fields.items ?? []);
        setRules(p?.rules.items ?? []);
        setMe(who ? { org: who.org, roles: who.roles } : null);
        setBatches(list);
        setOrgs((o.data ?? []) as Org[]);
      } catch (e) {
        setError(e);
      }
    })();
  }, []);

  const loadBatch = useCallback(async () => {
    if (!batchId) return;
    try {
      const { steps, prodCode: pc } = await batchChain(batchId);
      const nodes = steps
        .filter((s): s is typeof s & { id: string } => !!s.id)
        .map((s) => ({ kind: s.sameAs ?? s.kind, id: s.id }));
      setChain(nodes);
      setProdCode(pc);
      setValues(await valuesFor([...nodes.map((n) => n.id), ...(pc ? [pc] : [])]));
      setLoaded(batchId);
    } catch (e) {
      setError(e);
    }
  }, [batchId]);
  useEffect(() => {
    void loadBatch();
  }, [loadBatch]);

  const template = useMemo(
    () =>
      fields ? templateRows(fields, rules, org, chain, prodCode, values) : { rows: [], notIngestible: [] },
    [fields, rules, org, chain, prodCode, values],
  );

  if (!fields || !me) return <Loading />;
  const canWrite = me.roles.some((r) => WRITER_ROLES.includes(r));

  async function download() {
    const header = COLUMNS.map((c) => ({
      value: t(`template.col.${c}`),
      fontWeight: 'bold' as const,
      backgroundColor: '#E5F2ED',
      wrap: true,
    }));
    const fill = (v: string) => ({ value: v, backgroundColor: '#FFF4D6' });
    const data = [
      header,
      ...template.rows.map((r) => [
        r.field_id,
        r.field_name,
        r.node_kind,
        r.node_id,
        r.unit,
        r.allowed,
        r.current === null ? '' : fmtValue(r.current),
        r.current_status,
        fill(''),
        fill(''),
        fill(''),
        fill('final'),
      ]),
    ];
    const help = [
      [
        {
          value: t('template.help.title', { org: org?.name ?? t('template.all_orgs'), batch: batchId }),
          fontWeight: 'bold' as const,
        },
      ],
      ...[1, 2, 3, 4, 5, 6, 7].map((n) => [t(`template.help.${n}`)]),
      [''],
      [{ value: t('template.help.nodes'), fontWeight: 'bold' as const }],
      ...chain.map((n) => [`${n.kind}: ${n.id}`]),
      ...(prodCode ? [[`product: ${prodCode}`]] : []),
    ];
    await writeExcelFile([
      {
        data,
        sheet: t('template.sheet.values'),
        columns: [20, 36, 14, 18, 14, 28, 22, 12, 22, 12, 24, 12].map((width) => ({ width })),
        stickyRowsCount: 1,
      },
      { data: help, sheet: t('template.sheet.help'), columns: [{ width: 110 }] },
    ]).toFile(`W2G-template-${org?.org_code ?? 'ALL'}-${batchId}.xlsx`);
  }

  async function read(file: File) {
    setError(null);
    setResults([]);
    try {
      const data = (await readSheet(file)) as unknown[][];
      setParsed(parseSheet(data, fields ?? [], template.rows, chain, prodCode, t));
    } catch (e) {
      setError(e);
    }
  }

  async function insert(r: Omit<ParsedRow, 'line' | 'problems'>): Promise<Result> {
    const { data, error: err } = await operatorClient()
      .from('field_values')
      .insert({
        node_kind: r.node_kind,
        node_id: r.node_id,
        field_id: r.field_id,
        value: r.value,
        unit: r.unit || null,
        value_status: r.value_status || null,
        source_document_ref: r.source_document || null,
        provenance: { acquisition_method: 'manual', source_document_ref: r.source_document || null },
      })
      .select('version, entered_at')
      .single();
    const name = fieldDef(fields ?? [], r.field_id)?.field_name ?? r.field_id;
    if (err)
      return {
        ok: false,
        text: `${name} (${r.node_id}): ${explainRefusal(err.message, fieldDef(fields ?? [], r.field_id)?.source_partner, t)}`,
      };
    const row = data as { version: number; entered_at: string };
    return {
      ok: true,
      text: `${name} (${r.node_id}): ${t('ingest.saved', { version: row.version })} ${new Date(row.entered_at).toLocaleString()}`,
    };
  }

  async function sendAll() {
    if (!parsed) return;
    setBusy(true);
    const out: Result[] = [];
    for (const r of parsed.filter((p) => p.problems.length === 0)) out.push(await insert(r));
    setResults(out);
    setParsed(null);
    setBusy(false);
    await loadBatch();
  }

  async function example(e: typeof EXAMPLE_ACCEPTED) {
    setResults([await insert(e)]);
  }

  const ready = parsed?.filter((p) => p.problems.length === 0).length ?? 0;

  return (
    <main>
      <PageHeader title={t('ingest.title')} page="ingest" />
      <ErrorBox error={error} />
      {!canWrite && <p className="hint">{t('ingest.read_only')}</p>}

      <section className="card">
        <h2>{t('template.choose')}</h2>
        <div className="form">
          <label>
            {t('template.org')}
            <select value={orgCode} disabled={ownOnly} onChange={(e) => set('org', e.target.value)}>
              {!ownOnly && <option value="">{t('template.all_orgs')}</option>}
              {orgs.map((o) => (
                <option key={o.org_code} value={o.org_code}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('template.batch')}
            <select value={batchId} onChange={(e) => set('batch', e.target.value)}>
              {batches.map((b) => (
                <option key={b.node_id} value={b.node_id}>
                  {b.node_id} · {b.prod_code}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="muted small">
          {t('template.chain')}:{' '}
          {chain.map((n) => (
            <span key={n.id}>
              <Enum ns="kind" v={n.kind} /> {n.id} ·{' '}
            </span>
          ))}
          {prodCode && (
            <>
              <Enum ns="kind" v="product" /> {prodCode}
            </>
          )}
        </p>
      </section>

      <div className="grid-2">
        <section className="card">
          <h2>{t('template.step1')}</h2>
          <p>
            {t('template.step1_text', {
              count: template.rows.length,
              org: org?.name ?? t('template.all_orgs'),
            })}
          </p>
          <button disabled={loaded !== batchId || template.rows.length === 0} onClick={() => void download()}>
            {t('template.download')}
          </button>
          {template.notIngestible.length > 0 && (
            <p className="muted small">
              {t('template.not_ingestible', { count: template.notIngestible.length })}{' '}
              {template.notIngestible.map((f) => f.field_name).join(', ')}
            </p>
          )}
        </section>
        <section className="card">
          <h2>{t('template.step2')}</h2>
          <p>{t('template.step2_text')}</p>
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={!canWrite || loaded !== batchId}
            onChange={(ev) => ev.target.files?.[0] && void read(ev.target.files[0])}
          />
        </section>
      </div>

      {parsed && (
        <section className="card">
          <h2>{t('template.check', { count: parsed.length })}</h2>
          {parsed.length === 0 ? (
            <p className="muted">{t('template.empty')}</p>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>{t('template.col.line')}</th>
                    <th>{t('template.col.field_name')}</th>
                    <th>{t('template.col.node_id')}</th>
                    <th>{t('template.col.new_value')}</th>
                    <th>{t('template.col.source_document')}</th>
                    <th>{t('template.col.check')}</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((p) => (
                    <tr key={p.line}>
                      <td>{p.line}</td>
                      <td>
                        {fieldDef(fields, p.field_id)?.field_name ?? p.field_id}
                        <br />
                        <code className="small">{p.field_id}</code>
                      </td>
                      <td>{p.node_id}</td>
                      <td>
                        {fmtValue(p.value)} {p.unit ?? ''}
                      </td>
                      <td>{p.source_document}</td>
                      <td className={p.problems.length ? 'error' : 'ok'}>
                        {p.problems.length ? p.problems.join('; ') : t('template.ready')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button disabled={busy || ready === 0} onClick={() => void sendAll()}>
                {t('template.send', { count: ready })}
              </button>
              <button className="secondary" onClick={() => setParsed(null)}>
                {t('template.cancel')}
              </button>
            </>
          )}
        </section>
      )}

      {results.length > 0 && (
        <section className="card" aria-live="polite">
          <h2>{t('ingest.recent')}</h2>
          <ul>
            {results.map((r, i) => (
              <li key={i} className={r.ok ? 'ok' : 'error'}>
                {r.text}
              </li>
            ))}
          </ul>
          <p className="small muted">
            {t('ingest.next')} <Link to="/values">{t('nav.values')}</Link>
          </p>
        </section>
      )}

      <OneValue
        rows={template.rows}
        disabled={!canWrite || loaded !== batchId}
        onSend={async (r) => setResults([await insert(r)])}
      />

      <details className="card">
        <summary>{t('ingest.examples')}</summary>
        <p className="muted small">{t('steps.1')}</p>
        <button disabled={!canWrite} onClick={() => void example(EXAMPLE_ACCEPTED)}>
          {t('ingest.example_ok')}
        </button>
        <button className="secondary" disabled={!canWrite} onClick={() => void example(EXAMPLE_REFUSED)}>
          {t('ingest.example_refused')}
        </button>
      </details>
    </main>
  );
}

function OneValue({
  rows,
  disabled,
  onSend,
}: {
  rows: TemplateRow[];
  disabled: boolean;
  onSend: (r: Omit<ParsedRow, 'line' | 'problems'>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [fid, setFid] = useState('');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState('');
  const [source, setSource] = useState('');
  const row = rows.find((r) => r.field_id === fid) ?? rows[0];
  return (
    <section className="card">
      <h2>{t('ingest.manual')}</h2>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!row) return;
          void onSend({
            field_id: row.field_id,
            node_kind: row.node_kind,
            node_id: row.node_id,
            value: row.list
              ? value
                  .split(';')
                  .map((s) => s.trim())
                  .filter(Boolean)
              : value,
            unit: unit || row.unit || null,
            source_document: source,
            value_status: 'final',
          });
        }}
      >
        <label>
          {t('template.col.field_name')}
          <select value={row?.field_id ?? ''} onChange={(e) => setFid(e.target.value)}>
            {rows.map((r) => (
              <option key={`${r.field_id}|${r.node_id}`} value={r.field_id}>
                {r.field_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('template.col.node_id')}
          <input value={row ? `${row.node_kind}: ${row.node_id}` : ''} readOnly />
        </label>
        <label>
          {t('template.col.new_value')}
          {row?.allowed && <span className="help">{row.allowed}</span>}
          <input required value={value} onChange={(e) => setValue(e.target.value)} />
        </label>
        <label>
          {t('template.col.new_unit')}
          <input value={unit} placeholder={row?.unit ?? ''} onChange={(e) => setUnit(e.target.value)} />
        </label>
        <label>
          {t('template.col.source_document')}
          <input required value={source} onChange={(e) => setSource(e.target.value)} />
        </label>
        <button type="submit" disabled={disabled || !row}>
          {t('ingest.submit')}
        </button>
      </form>
    </section>
  );
}
